<script setup lang="ts">
import clickOutside from 'click-outside';
import BreadcrumbsSimple from '@/components/BreadcrumbsSimple.vue';
import FilmTabs from '@/components/videos/FilmTabs.vue';
import VideoPlayer from '@/components/videos/VideoPlayer.vue';
import CategoryBubble from '@/components/CategoryBubble.vue';
import { useAreas, useCategories, useContentRefData } from '@ilteducation/content-ref-data';
import { computed, ref, watch, watchEffect } from 'vue';
import { RouteLocationRaw, useRoute } from 'vue-router';
import { useLocale } from '@/plugins/translation';
import type { Video } from '@ilteducation/videos';
import { useVideos } from '@ilteducation/videos';
import { storeToRefs } from 'pinia';
import router from '@/router';
import { useUserContext } from '@ilteducation/ilt-auth';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import type { ContentNotFoundEventPayloadV1, NewEvent } from '@ilteducation/activity-stream-types';
import { sendEvent } from '@ilteducation/activity-stream-events';
import { useExerciseStore } from '@/store/exercises';
import useQuizStore from '@/store/quiz';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { CollectionSelectOwnDialog, CollectionEditOwnDialog } from '@ilteducation/content-collections';
import config from '@/config/ConfigProvider';
import { ContentTypes } from '@ilteducation/components';
import { useAddToCollection } from '@/composables/use-add-to-collection';
import useGuiStore from '@/store/gui';
import VideosInAreaList from '@/components/videos/VideosInAreaList.vue';
import ShareDialog from '@/components/ShareDialog.vue';
import { useDisplay } from 'vuetify';
import axios from 'axios';
import { useConfigHelper } from '@ilteducation/config';

const { areas } = useAreas();
const { categories } = useCategories();
const videoStore = useVideos();
const exerciseStore = useExerciseStore();
const { getVideoBySlug, getVideosByAreaId } = videoStore;
const { realm, user } = useUserContext();
const { getApiUrl } = useConfigHelper();

const quizStore = useQuizStore();
const guiStore = useGuiStore();
const { settings } = storeToRefs(guiStore);
const route = useRoute();
const { t, l } = useLocale();
const { mdAndUp, smAndUp } = useDisplay();

const showShareDialog = ref(false);

const subject = computed(() => categories.value.find((cat) => cat.slug === route.params.subject));
const { isLoading } = useContentRefData();
const area = computed(
  () =>
    areas.value.find((a) => a.slug === route.params.area && a.categoryId === subject.value?.id) ??
    areas.value.find((a) => a.slug === route.params.area),
);

const areaVideos = ref<Video[]>([]);
const video = ref<Video>();
const videoFetched = ref(false);
const areaComputed = ref(false);
const videoRating = ref<1 | 0 | null>(null);

const fetchRating = async (contentId: string | number) => {
  videoRating.value = null;
  try {
    const apiUrl = getApiUrl('customer-ratings', true);
    const { data } = await axios.get(`${apiUrl}/ratings/video/${contentId}`);
    videoRating.value = data?.rating === 1 || data?.rating === 0 ? data.rating : null;
  } catch (error) {
    // No previous rating (e.g. 404) or request failed — leave unrated.
    videoRating.value = null;
  }
};

const showSkipIntroButton = computed(() => {
  const filmId = video.value?.filmId ?? '';
  return /^\d+$/.test(filmId);
});

const showTabContent = ref<boolean>(false);

const breadcrumbsSteps = computed(() => [
  {
    name: subject.value?.category ?? '',
    path: `/videos/${subject.value?.slug}`,
  },
  {
    name: area.value?.name ?? '',
    path: `/videos/${subject.value?.slug}#${area.value?.slug}`,
  },
]);

const collection = useAddToCollection({
  contentReference: computed(() => ({
    id: video.value?.id ?? '',
    type: ContentTypes.Video,
    categoryId: (video.value?.videos ?? []).length > 1 ? subject.value?.id : undefined,
  })),
  context: config.value('context'),
  extraDialogRefs: [showShareDialog],
});
const { showSelectDialog, showCreateDialog, contentReference: videoReference } = collection;

const clickPlayer = (e: MouseEvent) => {
  const container = document.querySelector('.js_video-controls-menu.is-active');
  if (container) {
    clickOutside(container, () => {
      if (e.target instanceof Element && e.target?.classList.contains('js_video-preserve-delete-is-active')) {
        return;
      }
      container.classList.remove('is-active');
    });
  }
};
const checkIfFound = () => {
  const videoAvailability = video.value?.videos?.find(
    (v) => v.market === realm && v.categoryId === subject.value?.id && v.areaId === area.value?.id,
  );
  const slugMatch = video.value?.slug === route.params.film;
  if (videoAvailability && slugMatch) {
    return;
  }
  const payload: ContentNotFoundEventPayloadV1 = {
    redirectContent: undefined,
    url: new URL(route.path, window.location.origin).href,
  };

  const logAndRedirect = (location: RouteLocationRaw = { name: 'notFound' }) => {
    const videoNotFoundEvent: NewEvent = {
      type: 'ContentNotFoundEventV1',
      payload,
      sourceApplication: 'manabua',
      partitionKey: user.uid,
    };
    sendEvent(videoNotFoundEvent);
    router.push(location);
  };

  if (!video.value) {
    payload.reason = `Video slug not found: ${route.params.film}`;
    logAndRedirect();
    return;
  }

  payload.redirectContent = {
    type: 'Video',
    id: video.value.id,
    categoryId: subject.value?.id,
    areaId: area.value?.id,
  };

  const goToAlternative = (reason: string, altVideo?: any, altCategory?: Category, altArea?: Area) => {
    payload.reason = reason;
    if (!altVideo || !altCategory || !altArea) {
      logAndRedirect();
      return;
    }
    payload.redirectUrl = new URL(
      `/videos/${altCategory.slug}/${altArea.slug}/${route.params.film}`,
      window.location.origin,
    ).href;
    logAndRedirect({
      name: 'showFilm',
      params: {
        subject: altCategory.slug,
        area: altArea.slug,
        film: route.params.film,
      },
    });
  };

  if (!slugMatch) {
    payload.reason = `Old video slug "${route.params.film}" used for Video ID ${video.value.id}`;
    logAndRedirect({
      name: 'showFilm',
      params: {
        subject: subject.value?.slug,
        area: area.value?.slug,
        film: video.value.slug,
      },
    });
    return;
  }

  if (!subject.value && !area.value) {
    const altVideo = video.value.videos?.find((v) => v.market === realm);
    const altArea = areas.value.find((a) => a.id === altVideo?.areaId);
    const altCategory = categories.value.find((c) => c.id === altVideo?.categoryId);
    goToAlternative(
      `Category (${route.params.subject}) and area (${route.params.area}) slug not found.`,
      altVideo,
      altCategory,
      altArea,
    );
  } else if (!subject.value) {
    const altVideo = video.value.videos?.find((v) => v.areaId === area.value?.id);
    const altCategory = categories.value.find((c) => c.id === altVideo?.categoryId);
    goToAlternative(`Category slug not found: ${route.params.subject}`, altVideo, altCategory, area.value);
  } else if (!area.value || !videoAvailability) {
    const altVideo = video.value.videos?.find((v) => v.categoryId === subject.value?.id);
    const altArea = areas.value.find((a) => a.id === altVideo?.areaId);
    goToAlternative(
      `Area slug ${area.value ? 'incorrect' : 'not found'}: ${route.params.area}`,
      altVideo,
      subject.value,
      altArea,
    );
  }
};

watch(
  () => area.value?.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      areaVideos.value = await getVideosByAreaId(newId);
    }
  },
  { immediate: true },
);

watch(
  [() => route.params.subject, () => route.params.area, () => route.params.film],
  async () => {
    if (route.params.film && (!video.value || video.value.slug !== route.params.film)) {
      quizStore.reset();
    }

    if (route.params.film) {
      video.value = await getVideoBySlug(route.params.film as string);
      videoFetched.value = true;
      if (video.value) {
        fetchRating(video.value.id);
      }
    }

    if (video.value && subject.value) {
      await exerciseStore.getExercisesWithProgress(video.value.resourceId);
    }
  },
  { immediate: true },
);
watchEffect(() => {
  // watch is not triggered when the value changes from undefined to undefined (area slug is not found)
  // watchEffect is a lower-level API in Vue that triggers whenever any dependency inside the effect changes, even if the value remains the same
  const _currentArea = area.value;
  areaComputed.value = true;
});

watch([areaComputed, videoFetched, () => isLoading('areas')], () => {
  // To perform checkIfFound on a video, we want to make sure the video is fetched and the subject & area are computed
  // Area computation depends on a subject computation, so we can only check if the area is computed
  if (isLoading('areas')) {
    return;
  }
  if (videoFetched.value && areaComputed.value) {
    checkIfFound();
  }
});

const openShareDialog = () => {
  collection.rememberFocus();
  showShareDialog.value = true;
};

const closeShareDialog = () => {
  showShareDialog.value = false;
};

const { getThemeName } = useGuiStore();

const handleRate = async (payload: { contentId: string | number; value: 1 | 0 | null }) => {
  if (payload.value === null) return;

  try {
    const apiUrl = getApiUrl('customer-ratings', true);
    await axios.put(`${apiUrl}/ratings/video/${payload.contentId}`, {
      rating: payload.value,
    });
    videoRating.value = payload.value;
  } catch (error) {
    console.error('Failed to save rating:', error);
  }
};
</script>
<template>
  <v-sheet :theme="getThemeName(subject?.slug)">
    <v-container
      v-if="video && subject && area && !showTabContent"
      :fluid="true"
      class="video-container pt-0 px-0 bg-surface"
      :style="!mdAndUp ? 'min-height: calc(100vh - 64px); max-width: 800px;' : ''"
    >
      <template v-if="mdAndUp">
        <v-row class="breadcrumbs-section px-0 mx-0 mt-0">
          <v-container class="custom-container px-16">
            <v-row>
              <v-col md="8" class="d-flex align-self-end pb-0">
                <BreadcrumbsSimple :steps="breadcrumbsSteps" />
              </v-col>
              <v-col v-if="subject.categoryCode" md="4" class="d-flex justify-end">
                <CategoryBubble :subject="subject" size="medium-small" />
              </v-col>
            </v-row>
          </v-container>
        </v-row>
      </template>
      <template v-else>
        <v-container class="custom-container pt-0">
          <v-row>
            <v-col class="d-flex align-self-end pb-0">
              <BreadcrumbsSimple :steps="breadcrumbsSteps" />
            </v-col>
          </v-row>
        </v-container>
      </template>
      <v-row class="bg-surface w-100 px-0 mx-0" :class="{ 'bg-surface-container-lowest': mdAndUp }">
        <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
          <v-row class="gc-sm-8 gc-lg-16">
            <v-col
              class="px-2 px-sm-4 d-flex flex-column video-col"
              cols="12"
              :md="mdAndUp ? 7 : 12"
              @click="clickPlayer"
            >
              <div v-if="video" class="d-flex justify-space-between align-start mt-1 ga-4 order-title">
                <h1 class="mb-0 video-title flex-grow-1" :class="{ subheader: !mdAndUp }">
                  <span class="d-sr-only">{{ t(l.sr_only.film_page) }}</span>
                  {{ video.title }}
                </h1>
                <div class="d-flex align-start ga-2 flex-shrink-1">
                  <v-checkbox-btn
                    v-if="smAndUp && showSkipIntroButton"
                    v-model="settings.skipVideoIntros"
                    :label="t(l.film.skipIntros)"
                    class="checkbox"
                    color="secondary"
                    :aria-label="t(l.film.skipIntros)"
                  />

                  <v-tooltip location="top" :aria-hidden="true">
                    <template #activator="{ props }">
                      <v-btn
                        variant="text"
                        class="video-action-button"
                        @click="collection.openSelectDialog"
                        v-bind="props"
                        :aria-label="t(l.content_collections.tooltip_add_to_collection)"
                      >
                        <template #prepend>
                          <FontAwesomeIcon icon="fa-regular fa-plus" size="lg" />
                        </template>
                      </v-btn>
                    </template>
                    {{ t(l.content_collections.tooltip_add_to_collection) }}
                  </v-tooltip>

                  <v-tooltip location="top" :aria-hidden="true">
                    <template #activator="{ props }">
                      <v-btn
                        id="share-button"
                        variant="text"
                        class="video-action-button"
                        @click="openShareDialog"
                        v-bind="props"
                        :aria-label="t(l.film.shareVideo)"
                      >
                        <template #prepend>
                          <FontAwesomeIcon icon="fa-regular fa-share-from-square" size="lg" />
                        </template>
                      </v-btn>
                    </template>
                    {{ t(l.film.tooltip_share) }}
                  </v-tooltip>
                </div>
              </div>
              <VideoPlayer
                v-if="video && subject"
                :key="video.id"
                :video="video"
                :category="subject"
                :area="area"
                :rating="videoRating"
                class="order-player"
                @rate="handleRate"
              />
              <p v-if="video.description" class="short-description mb-2 order-description">{{ video.description }}</p>
            </v-col>
            <template v-if="mdAndUp">
              <VideosInAreaList :video="video" :area-videos="areaVideos" :area="area" :subject="subject" />
            </template>
          </v-row>
        </v-container>
      </v-row>
      <v-row class="bg-surface px-0 mx-0" :class="{ 'bg-surface-container-lowest': mdAndUp }">
        <FilmTabs v-if="video" :video="video" :area="area" :subject="subject" :area-videos="areaVideos" />
      </v-row>
      <ShareDialog
        v-if="showShareDialog"
        :item-to-share="video"
        :subject="subject.category"
        :title="t(l.film.shareVideo)"
        @close="closeShareDialog"
      ></ShareDialog>
      <CollectionSelectOwnDialog
        v-if="showSelectDialog"
        :content-references="[videoReference]"
        :context="collection.context"
        :model-value="showSelectDialog"
        :content-type="ContentTypes.Video"
        @close="collection.closeSelectDialog"
        @open-create-collection="collection.openCreateDialog"
        @update-collection="collection.handleUpdateCollection"
      />
      <CollectionEditOwnDialog
        v-if="showCreateDialog"
        :content-references="[videoReference]"
        :context="collection.context"
        :content-type="ContentTypes.Video"
        :selected-icon="'thinPhotoFilm'"
        :show-icons="false"
        :is-public="true"
        @create-collection="collection.handleCreateCollection"
        @close="collection.closeCreateDialog"
      />
    </v-container>
  </v-sheet>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

$light-blue: rgb(var(--v-theme-inverse-primary));

.video-title {
  font-size: 1.5rem;
  color: rgb(var(--v-theme-on-surface));
  min-width: 0;

  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    font-size: 1.875rem;
  }
}

.video-col {
  .order-player {
    order: 0;
  }
  .order-title {
    order: 1;
  }
  .order-description {
    order: 2;
  }
}

.video-container {
  .row {
    max-width: none;
    width: auto;
    margin-left: -12px;
    margin-right: -12px;
  }
}
.custom-container {
  max-width: 93rem !important;
}

.breadcrumbs-section {
  box-shadow: inset 0 -1px 0 0 rgb(var(--v-theme-outline-variant));

  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    border-bottom: 1px solid var(--v-gray-lighten2);
  }
}

.short-description {
  font-size: 1.125rem;
  overflow: hidden;

  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    white-space: normal;
  }
}

.missing-content {
  h3,
  .v-image {
    max-width: 640px;
    margin: 0 auto 40px;
  }
}

.video-action-button {
  min-width: var(--v-btn-height) !important;
  padding: 0;
  :deep(.v-btn__prepend) {
    margin: 0 !important;
  }
}

.checkbox {
  font-size: 0.875rem;
  min-height: 48px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  :deep(.v-label) {
    text-wrap: nowrap;
    color: rgb(var(--v-theme-on-surface-medium));
    transition: color 0.2s ease;
  }

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.04);

    :deep(.v-label) {
      color: rgb(var(--v-theme-on-surface));
    }
  }
}
</style>
